/**
 * JotRx Setup State Hook
 *
 * Derives the user's current JotRx onboarding state from the existing
 * eprescriptions status API, entitlement checks, and bundle catalog.
 */

import { useMemo } from 'react';
import { useEntitlement } from '@features/entitlements';
import { useBundleCatalog } from '@features/subscriptions';
import { useDosespotOnboardingStatus } from '@features/user';

import { useEPrescriptionStatus } from '../store/hooks';

/** Setup states matching the product spec */
export type JotRxSetupState =
  | 'not_connected'
  | 'practice_connected'
  | 'registered'
  | 'idp_complete'
  | 'fully_setup';

/** Where the CTA should route the user */
export interface JotRxSetupCta {
  label: string;
  route: string;
}

export interface JotRxSetupStateResult {
  /** Current setup state */
  setupState: JotRxSetupState;
  /** Whether the org has the JotRx entitlement */
  isJotRxEnabled: boolean;
  /** Whether JotRx is available for purchase in the bundle catalog */
  isJotRxPurchasable: boolean;
  /** Whether we're still loading status */
  isLoading: boolean;
  /** CTA config for the current state */
  cta: JotRxSetupCta;
  /** Whether the user has completed all setup */
  isFullySetup: boolean;
}

/**
 * Derives JotRx setup state from the eprescriptions status API.
 *
 * States:
 * - not_connected: No DoseSpot connection yet
 * - practice_connected: Clinic linked, clinician not registered
 * - registered: Clinician registered, identity proofing pending
 * - fully_setup: Ready to prescribe (identity proofing complete)
 */
export function useJotRxSetupState(): JotRxSetupStateResult {
  const { entitlement: jotrxEntitlement } = useEntitlement('jotrx');
  const isJotRxEnabled = !!jotrxEntitlement;
  const { isOrgConnected, isUserConnected, isLoading } = useEPrescriptionStatus();
  const { addOns, isLoading: isCatalogLoading } = useBundleCatalog();
  const dosespotOnboardingStatus = useDosespotOnboardingStatus();

  const isJotRxPurchasable = useMemo(
    () => addOns.some(b => b.name === 'JotRx'),
    [addOns],
  );

  return useMemo(() => {
    const isFullyOnboarded = dosespotOnboardingStatus === 'completed';
    const isIdpComplete = dosespotOnboardingStatus === 'tfa_needed';

    let setupState: JotRxSetupState = 'not_connected';
    if (isOrgConnected && isUserConnected && isFullyOnboarded) {
      setupState = 'fully_setup';
    } else if (isOrgConnected && isUserConnected && isIdpComplete) {
      setupState = 'idp_complete';
    } else if (isOrgConnected && isUserConnected) {
      setupState = 'registered';
    } else if (isOrgConnected) {
      setupState = 'practice_connected';
    }

    const isFullySetup = setupState === 'fully_setup';
    const cta = getCta(setupState, isJotRxEnabled);

    return {
      setupState,
      isJotRxEnabled,
      isJotRxPurchasable,
      isLoading: isLoading || isCatalogLoading,
      cta,
      isFullySetup,
    };
  }, [isOrgConnected, isUserConnected, isJotRxEnabled, isJotRxPurchasable, isLoading, isCatalogLoading, dosespotOnboardingStatus]);
}

function getCta(state: JotRxSetupState, hasEntitlement: boolean): JotRxSetupCta {
  switch (state) {
    case 'not_connected':
      if (hasEntitlement) {
        // Has JotRx but practice isn't connected to DoseSpot yet
        return { label: 'Continue setup', route: '/practice/settings' };
      }
      // No entitlement — go add JotRx to plan
      return { label: 'Set up now', route: '/practice/clinicians' };
    case 'practice_connected':
      // Practice connected, clinician needs to register
      return { label: 'Continue setup', route: '/user' };
    case 'registered':
      return { label: 'Continue setup', route: '/user' };
    case 'idp_complete':
      return { label: 'Set up TFA', route: '/user' };
    case 'fully_setup':
      // TODO: Open DoseSpot prescribing flow directly
      return { label: 'Prescribe', route: '/user' };
  }
}
